import { NgModule, Injector, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';

import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';

import { SlbNavigationFrameworkModule } from '@slb-dls/angular-material/navigation-framework';
import { SlbSharedModule } from '@slb-dls/angular-material/shared';
import { SlbNotificationModule, MessageService } from '@slb-dls/angular-material/notification';
import { SlbNotificationsPanelModule } from '@slb-dls/angular-material/notifications-panel';
import { SlbPopoverModule } from '@slb-dls/angular-material/popover';
import { SlbButtonModule } from '@slb-dls/angular-material/button';
import { SlbCardGroupModule } from '@slb-dls/angular-material/card-group';
import { SlbTabControlModule } from '@slb-dls/angular-material/tab-control';
import { SlbPanelListModule } from '@slb-dls/angular-material/panel-list';
import { SlbSplitterModule } from '@slb-dls/angular-material/splitter';
import { SlbTooltipModule } from '@slb-dls/angular-material/tooltip';
import { SLB_MOMENT_DATE_FORMATS, SLB_THEMING_OPTIONS } from '@slb-dls/angular-material/core';
import { SlbFormFieldModule } from '@slb-dls/angular-material/form-field';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { themeConfig } from '../themes/theme.config';

import { AppConfigService } from './app-config.service';
import { PageTitleService } from './page-title.service';

import { AngularSplitModule } from 'angular-split';

import { AcquisitionChartComponent } from './acquisition-chart/acquisition-chart.component';
import { DashboardDocsComponent } from './dashboard-docs/dashboard-docs.component';
import { DashboardVariablesComponent } from './dashboard-docs/dashboard-variables.component';
import { DashboardChannelsComponent } from './dashboard-docs/dashboard-channels.component';
import { SlbDropdownModule } from '@slb-dls/angular-material/dropdown';
import { CtUiChartModule } from '@ct-software/ct-ui-chart';
import { CtUiCoreModule } from '@ct-software/ct-ui-core';
import { FormsModule } from '@angular/forms';
import { ChannelListComponent } from './channel-list/channel-list.component';
import { FilterByInputStringPipe } from './channel-list/filter-by-input-string.pipe';

import { AppInjector } from '@ct-software/ct-ui-core';

export function initializeApp(appConfigService: AppConfigService) {
  return () => appConfigService.load();
}

const pipes = [
  FilterByInputStringPipe,
];

@NgModule({ declarations: [
        ...pipes,
        AppComponent,
        HomeComponent,
        DashboardComponent,
        NotificationsComponent,
        AcquisitionChartComponent,
        DashboardDocsComponent,
        DashboardVariablesComponent,
        DashboardChannelsComponent,
        ChannelListComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        CommonModule,
        FormsModule,
        ScrollingModule,
        MatCardModule,
        MatButtonModule,
        MatToolbarModule,
        MatIconModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatTabsModule,
        MatListModule,
        MatMenuModule,
        MatTableModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatInputModule,
        MatDialogModule,
        MatDividerModule,
        MatExpansionModule,
        MatRadioModule,
        MatSelectModule,
        SlbSharedModule,
        SlbButtonModule,
        SlbPopoverModule,
        SlbNotificationModule,
        SlbNotificationsPanelModule,
        SlbNavigationFrameworkModule,
        SlbCardGroupModule,
        SlbTabControlModule,
        SlbDropdownModule,
        SlbFormFieldModule,
        CtUiChartModule,
        CtUiCoreModule,
        AngularSplitModule,
        SlbSplitterModule,
        SlbTooltipModule,
        SlbPanelListModule], providers: [
        AppConfigService,
        PageTitleService,
        provideAppInitializer(() => {
        const initializerFn = (initializeApp)(inject(AppConfigService));
        return initializerFn();
      }),
        { provide: MAT_DATE_FORMATS, useValue: SLB_MOMENT_DATE_FORMATS },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MessageService, useClass: MessageService },
        { provide: SLB_THEMING_OPTIONS, useValue: themeConfig },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  constructor(injector: Injector) {
    AppInjector.injector = injector;
  }
}
